import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { IconRotateClockwise } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo, useState } from 'react';
import { BackgroundJobSummaryDto } from 'src/api/FiretrolApiClient';
import {
  useBackgroundJobOverview,
  useBackgroundJobs,
} from 'src/api/queries/admin/useBackgroundJobs';
import {
  useDeleteBackgroundJob,
  useRequeueBackgroundJob,
} from 'src/api/queries/admin/useBackgroundJobMutations';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import {
  IStatusSegment,
  WorkflowStatusPanel,
} from 'src/design/molecules/WorkflowStatusPanel';
import { formatDateTime } from 'src/utils';
import {
  canDeleteJob,
  canRequeueJob,
  DEFAULT_JOB_STATE,
  formatJobDuration,
  JOB_STATE_LABELS,
  jobStateColor,
  jobStateCount,
  jobStateFilterFor,
  jobStateTimestamp,
} from 'src/routes/Settings/BackgroundJobs/jobActions';
import { BackgroundJobDetailDrawer } from 'src/routes/Settings/BackgroundJobs/components/BackgroundJobDetailDrawer';

const getColumns = (
  state: string,
): MRT_ColumnDef<BackgroundJobSummaryDto>[] => {
  const columns: MRT_ColumnDef<BackgroundJobSummaryDto>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 110,
      Cell: ({ cell }) => (
        <Text size="xs" ff="monospace">
          {cell.getValue<string>()}
        </Text>
      ),
    },
    { accessorKey: 'jobName', header: 'Job', size: 320 },
    {
      accessorKey: 'state',
      header: 'State',
      size: 120,
      Cell: ({ cell }) => {
        const value = cell.getValue<string | undefined>();
        return value ? (
          <Badge color={jobStateColor(value)} size="sm">
            {value}
          </Badge>
        ) : null;
      },
    },
    {
      id: 'timestamp',
      header: 'When',
      size: 170,
      // Guarded because MRT evaluates accessorFns against empty objects while
      // the table renders skeleton rows.
      accessorFn: (row) => (row.state ? jobStateTimestamp(row) : undefined),
      Cell: ({ cell }) => formatDateTime(cell.getValue<Date | undefined>()),
    },
  ];

  if (state === 'Enqueued') {
    columns.push({ accessorKey: 'queue', header: 'Queue', size: 120 });
  }

  if (state === 'Succeeded') {
    columns.push({
      accessorKey: 'totalDurationMs',
      header: 'Duration',
      size: 110,
      Cell: ({ cell }) =>
        formatJobDuration(cell.getValue<number | undefined>()),
    });
  }

  if (state === 'Failed') {
    columns.push({
      accessorKey: 'exceptionMessage',
      header: 'Error',
      size: 320,
      Cell: ({ cell, row }) => (
        <Text size="xs" c="red" lineClamp={2}>
          {cell.getValue<string | undefined>() ??
            row.original.exceptionType ??
            '—'}
        </Text>
      ),
    });
  }

  return columns;
};

export const BackgroundJobsTable: React.FC = () => {
  const [state, setState] = useState(DEFAULT_JOB_STATE);
  const [detailId, setDetailId] = useState<string | null>(null);
  const requeueJob = useRequeueBackgroundJob();
  const deleteJob = useDeleteBackgroundJob();
  const overviewQuery = useBackgroundJobOverview();

  const statistics = overviewQuery.data?.statistics;
  const segments = useMemo<IStatusSegment[]>(
    () =>
      JOB_STATE_LABELS.map((label) => ({
        value: label,
        label,
        count: jobStateCount(statistics, label),
        color: jobStateColor(label),
      })),
    [statistics],
  );

  const columns = useMemo(() => getColumns(state), [state]);

  const tableState = useTableState({
    identifier: 'background-jobs',
    columns,
  });
  const { pagination, setPagination } = tableState;

  const jobsQuery = useBackgroundJobs(
    jobStateFilterFor(state),
    pagination.pageIndex + 1,
    pagination.pageSize,
  );
  const jobs = useMemo(() => jobsQuery.data?.items ?? [], [jobsQuery.data]);
  const totalCount = jobsQuery.data?.totalCount ?? 0;

  // A new state filter has its own result set, so the page number from the
  // previous one may no longer exist. Both updates happen in this one event so
  // the table never requests the new state at the old page.
  const handleStateChange = (next: string) => {
    setState(next);
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  };

  const { table } = useTable({
    ...tableState,
    onRefresh: jobsQuery.refetch,
    tableOptions: {
      enableColumnFilters: false,
      enableRowActions: true,
      enableClickToCopy: false,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 140 } },
      columns,
      data: jobs,
      manualPagination: true,
      manualSorting: false,
      manualFiltering: true,
      renderRowActions: ({ row }) => {
        const job = row.original;
        if (!job.id) return null;
        // `isPending` is shared by every row, so it is narrowed to the id the
        // mutation is actually carrying — otherwise all rows spin at once.
        const requeueing =
          requeueJob.isPending && requeueJob.variables === job.id;
        return (
          <Group gap="xs">
            <Tooltip label="Details">
              <ViewActionIcon
                variant="light"
                onClick={() => setDetailId(job.id)}
              />
            </Tooltip>
            {canRequeueJob(job.state) && (
              <Tooltip label="Requeue">
                <BasicActionIcon
                  variant="light"
                  color="teal"
                  loading={requeueing}
                  onClick={() => requeueJob.mutate(job.id)}
                >
                  <IconRotateClockwise size={18} />
                </BasicActionIcon>
              </Tooltip>
            )}
            {canDeleteJob(job.state) && (
              <DeleteActionIcon
                requiredConfirmation
                confirmationText={`Delete job ${job.id} (${job.jobName})?`}
                onDelete={() => deleteJob.mutateAsync(job.id)}
              />
            )}
          </Group>
        );
      },
    },
    getRowId: (row) => row.id,
    totalCount,
    isLoading: jobsQuery.isLoading,
    isFetching: jobsQuery.isFetching,
    isError: jobsQuery.isError,
  });

  return (
    <>
      {/* The jobs endpoint has no "all states" filter, so the active state has to stay selected. */}
      <WorkflowStatusPanel
        segments={segments}
        value={state}
        onChange={handleStateChange}
        allowDeselect={false}
        ariaLabel="Filter by job state"
      />
      {table}
      <BackgroundJobDetailDrawer
        jobId={detailId}
        onClose={() => setDetailId(null)}
      />
    </>
  );
};
